import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, TextField, Autocomplete, Button, Chip, CircularProgress, Alert } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { fetchPmFrameList, fetchPmDelayReasons } from "../actions/ProductionMonitorAction";


const NAVY = "#142c54";
const BLUE = "#154c79";

// SHELL_GIRDER -> Shell Girder, DECK_01 -> Deck 01
// names that don't follow the simple "_" -> space rule
const COMPONENT_NAMES = { EDGE_GRINDING: "EDGE Grinding" };

const componentLabel = (v) =>
  COMPONENT_NAMES[v] ||
  String(v || "")
    .split("_")
    .map((w) => (w.length > 3 ? w.charAt(0) + w.slice(1).toLowerCase() : w))
    .join(" ");

const toYmd = (v) => String(v || "").slice(0, 10);

const toNumber = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const cardSx = {
  p: { xs: 1.5, sm: 2.5 },
  borderRadius: "16px",
  border: "1px solid #d6dce5",
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } };

// Same CDL reason groups as Update Daily Remark
const CDL_REASON_GROUPS = {
  SF: "SWC Workshop Delay",
  SC: "SWC Workshop Delay",
  SB: "SWC Workshop Delay",
  ST: "SWC Workshop Delay",
  SM: "SWC Workshop Delay",
  SL: "SWC Workshop Delay",
  IP: "Inspection Delay",
  IQ: "Inspection Delay",
  IO: "Inspection Delay",
};
const cdlGroupOf = (code) => CDL_REASON_GROUPS[code] || "";

// "SWC Workshop Delay · Material Delay" (or just the name when no group)
const reasonText = (list, code, groupOf) => {
  if (!code) return "";
  const name = (list || []).find((d) => d.Code === code)?.Name || code;
  const group = groupOf ? groupOf(code) : "";
  return group ? `${group} · ${name}` : name;
};

/* ---- One remark ---- */
const RemarkCard = ({ r, dtsDelayReasons, cdlDelayReasons }) => (
  <Box sx={{ p: 1.5, borderRadius: "12px", border: "1px solid #eef1f5", backgroundColor: "#fafbfd" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0E3C66" }}>{toYmd(r.WorkDate)}</Typography>
      <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
        <Chip label={`DTS ${toNumber(r.LostHoursDts)} h`} size="small" sx={{ height: 22, fontSize: "11px" }} />
        <Chip label={`CDL ${toNumber(r.LostHoursCdl)} h`} size="small" sx={{ height: 22, fontSize: "11px" }} />
      </Box>
    </Box>
    <Typography sx={{ fontSize: "13px", color: "#333", mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {r.Remark}
    </Typography>
    {[
      { label: "DTS", code: r.DtsDelayReason || r.DtsReason, remark: r.DtsRemark, list: dtsDelayReasons },
      { label: "CDL", code: r.CdlDelayReason || r.CdlReason, remark: r.CdlRemark, list: cdlDelayReasons, groupOf: cdlGroupOf },
    ]
      .filter((x) => x.code || x.remark)
      .map((x) => (
        <Typography key={x.label} sx={{ fontSize: "12px", color: "#555", mt: 0.5 }}>
          <b style={{ color: NAVY }}>{x.label}:</b> {reasonText(x.list, x.code, x.groupOf)}
          {x.code && x.remark ? " — " : ""}
          {x.remark}
        </Typography>
      ))}
    <Typography sx={{ fontSize: "11px", color: "#9aa3b2", mt: 0.5 }}>
      By {r.CreatedBy} · {r.CreatedDate}
    </Typography>
  </Box>
);

/* -------------------- Page -------------------- */
const RemarkHistory = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { frameList, frameListLoading, frameListError, cdlDelayReasons, dtsDelayReasons } = useSelector(
    (state) => state.productionMonitor
  );

  // what was selected on Update Daily Remark (kept for the Back button)
  const fromIwoNo = params.get("iwoNo") || null;
  const fromFrameId = params.get("frameId") ? Number(params.get("frameId")) : null;

  const [iwoNo, setIwoNo] = useState(fromIwoNo);
  const [component, setComponent] = useState(null); // null = all components of the IWO
  const [frameId, setFrameId] = useState(fromFrameId); // null = all frames of the component

  useEffect(() => {
    dispatch(fetchPmFrameList());
    dispatch(fetchPmDelayReasons());
  }, [dispatch]);

  const frames = useMemo(
    () =>
      [...(frameList || [])].sort(
        (a, b) =>
          String(a.IwoNo).localeCompare(String(b.IwoNo), undefined, { numeric: true }) ||
          String(a.FrameNo).localeCompare(String(b.FrameNo), undefined, { numeric: true })
      ),
    [frameList]
  );

  // one row per IWO with Eng Spec + remark count
  const iwoOptions = useMemo(() => {
    const map = new Map();
    frames.forEach((f) => {
      const key = String(f.IwoNo);
      if (!map.has(key)) map.set(key, { IwoNo: key, EngSpec: f.EngSpec || "", frames: 0, remarks: 0 });
      const row = map.get(key);
      row.frames += 1;
      row.remarks += (f.DailyRemarks || []).length;
      if (!row.EngSpec && f.EngSpec) row.EngSpec = f.EngSpec;
    });
    return Array.from(map.values());
  }, [frames]);

  const iwo = iwoOptions.find((o) => o.IwoNo === iwoNo) || null;
  const iwoFrames = useMemo(() => (iwoNo ? frames.filter((f) => String(f.IwoNo) === iwoNo) : []), [frames, iwoNo]);

  // components of the chosen IWO (with frame / remark counts)
  const componentOptions = useMemo(() => {
    const map = new Map();
    iwoFrames.forEach((f) => {
      const key = f.ComponentName || "";
      if (!map.has(key)) map.set(key, { value: key, frames: 0, remarks: 0 });
      const row = map.get(key);
      row.frames += 1;
      row.remarks += (f.DailyRemarks || []).length;
    });
    return Array.from(map.values());
  }, [iwoFrames]);
  const componentOption = componentOptions.find((c) => c.value === component) || null;

  // frames of the chosen component (or all of the IWO when no component is chosen)
  const componentFrames = useMemo(
    () => (component ? iwoFrames.filter((f) => (f.ComponentName || "") === component) : iwoFrames),
    [iwoFrames, component]
  );
  const frame = componentFrames.find((f) => f.FrameId === frameId) || null;

  // coming from Daily Remark with a frame -> also select that frame's component
  useEffect(() => {
    if (component === null && frameId) {
      const f = iwoFrames.find((x) => x.FrameId === frameId);
      if (f) setComponent(f.ComponentName || "");
    }
  }, [iwoFrames, frameId, component]);

  // frames to show: the chosen one, or every frame (of the component / IWO) that has remarks
  const groups = (frame ? [frame] : componentFrames)
    .map((f) => ({
      frame: f,
      remarks: [...(f.DailyRemarks || [])].sort((a, b) => String(b.WorkDate).localeCompare(String(a.WorkDate))),
    }))
    .filter((g) => frame || g.remarks.length > 0);

  const all = groups.flatMap((g) => g.remarks);
  const totalDts = all.reduce((sum, r) => sum + toNumber(r.LostHoursDts), 0);
  const totalCdl = all.reduce((sum, r) => sum + toNumber(r.LostHoursCdl), 0);

  // back to Update Daily Remark with the same IWO / frame it came from
  const goBack = () => {
    const q = new URLSearchParams();
    if (fromIwoNo) q.set("iwoNo", fromIwoNo);
    if (fromIwoNo && fromFrameId) q.set("frameId", String(fromFrameId));
    navigate(`/dailyRemark${q.toString() ? `?${q}` : ""}`);
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
      <HeaderBannerPage />

      <Box
        sx={{
          maxWidth: "900px",
          width: "100%",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {/* Title + back */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <HistoryIcon sx={{ color: BLUE }} />
          <Typography sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 700, color: "#0E3C66", letterSpacing: "0.5px" }}>
            Remark History
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            sx={{ ml: "auto", textTransform: "none", fontWeight: 700, borderRadius: "10px", color: BLUE, borderColor: "#c9d6e6", backgroundColor: "#fff" }}
          >
            Daily Remark
          </Button>
        </Box>

        {/* IWO + frame */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 1.5 }}>
            {/* 1. IWO */}
            <Autocomplete
              sx={{ gridColumn: "1 / -1" }}
              options={iwoOptions}
              value={iwo}
              onChange={(_, v) => {
                setIwoNo(v ? v.IwoNo : null);
                setComponent(null);
                setFrameId(null);
              }}
              loading={frameListLoading}
              autoHighlight
              getOptionLabel={(o) => (o ? `${o.IwoNo}${o.EngSpec ? ` - ${o.EngSpec}` : ""}` : "")}
              isOptionEqualToValue={(o, v) => o.IwoNo === v.IwoNo}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) => [o.IwoNo, o.EngSpec].some((v) => String(v || "").toLowerCase().includes(q)));
              }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{o.IwoNo}</Typography>
                    <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>
                      {o.frames} frame(s) · {o.remarks} remark(s)
                    </Typography>
                  </Box>
                  {o.EngSpec && (
                    <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "normal", wordBreak: "break-word" }}>{o.EngSpec}</Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IWO No - Eng Spec"
                  placeholder="Search IWO no or eng spec"
                  helperText={iwo?.EngSpec || ""}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {frameListLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* 2. Component */}
            <Autocomplete
              options={componentOptions}
              value={componentOption}
              onChange={(_, v) => {
                setComponent(v ? v.value : null);
                setFrameId(null);
              }}
              disabled={!iwoNo}
              autoHighlight
              getOptionLabel={(c) => (c ? componentLabel(c.value) || "-" : "")}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              noOptionsText="No components for this IWO"
              renderOption={(props, c) => (
                <Box component="li" {...props} key={c.value} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>{componentLabel(c.value) || "-"}</Typography>
                  <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280", whiteSpace: "nowrap" }}>
                    {c.frames} frame(s) · {c.remarks} remark(s)
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Component"
                  placeholder={iwoNo ? "All components" : "Select an IWO first"}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              )}
            />

            {/* 3. Frame */}
            <Autocomplete
              options={componentFrames}
              value={frame}
              onChange={(_, v) => setFrameId(v ? v.FrameId : null)}
              disabled={!iwoNo || component === null}
              autoHighlight
              getOptionLabel={(f) => (f ? `${f.FrameNo ? `${f.FrameNo} · ` : ""}${componentLabel(f.ComponentName)}` : "")}
              isOptionEqualToValue={(o, v) => o.FrameId === v.FrameId}
              noOptionsText="No frames for this IWO"
              renderOption={(props, f) => (
                <Box component="li" {...props} key={f.FrameId} sx={{ display: "flex !important", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>{f.FrameNo || "—"}</Typography>
                  <Chip label={componentLabel(f.ComponentName)} size="small" sx={{ height: 20, fontSize: "11px" }} />
                  <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280" }}>{(f.DailyRemarks || []).length} remark(s)</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Frame"
                  placeholder={!iwoNo ? "Select an IWO first" : component === null ? "Select a component first" : "All frames"}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              )}
            />
          </Box>
        </Paper>

        {frameListError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{frameListError}</Alert>}

        {!iwoNo ? (
          <Paper elevation={0} sx={cardSx}>
            <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 5, textAlign: "center" }}>Select an IWO to see its remarks</Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={cardSx}>
            {/* Totals */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, mb: 2 }}>
              {[
                { label: "Entries", value: all.length },
                { label: "Lost DTS", value: `${totalDts} h` },
                { label: "Lost CDL", value: `${totalCdl} h` },
              ].map((t) => (
                <Box key={t.label} sx={{ p: 1, borderRadius: "10px", backgroundColor: "#f3f6fa", textAlign: "center" }}>
                  <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{t.label}</Typography>
                  <Typography sx={{ fontSize: "15px", fontWeight: 700, color: NAVY }}>{t.value}</Typography>
                </Box>
              ))}
            </Box>

            {all.length === 0 ? (
              <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 4, textAlign: "center" }}>
                {frame ? "No remarks yet for this frame" : "No remarks yet for this IWO"}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {groups.map((g) => (
                  <Box key={g.frame.FrameId}>
                    {/* frame heading */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{g.frame.FrameNo || "—"}</Typography>
                      <Chip label={componentLabel(g.frame.ComponentName)} size="small" sx={{ height: 20, fontSize: "11px" }} />
                      <Typography sx={{ ml: "auto", fontSize: "11px", color: "#6b7280" }}>
                        {g.remarks.length} remark(s) · {Math.round(toNumber(g.frame.PhysicalCompletion))}% done
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {g.remarks.map((r, i) => (
                        <RemarkCard
                          key={r.DailyId || `${g.frame.FrameId}-${i}`}
                          r={r}
                          dtsDelayReasons={dtsDelayReasons}
                          cdlDelayReasons={cdlDelayReasons}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default RemarkHistory;